// How far the user has listened to each track — the memory behind the For You
// hero cards' "heard" waveform and the lead card's "Resume 1:12" button.
//
// One record per trackId: { p: furthest second heard, l: last position,
// d: duration of the source that played, t: last played (ms), done, b: a
// 96-bar 0-99 peaks summary so the frozen waveform paints after a restart
// without re-downloading anything }. Stored in localStorage, capped, and wiped
// on logout / account switch (it is listening history, so it is account data).
//
// The recorder is ONE module-level 1 s ticker started by MusicPlayer: it reads
// the active player's clock (the <audio> element on desktop/web, the native
// plugin's mirrored position on phones) and the window.__currentPlayer*
// globals the player already stashes on every track change. Mixes are skipped.
import { useEffect, useState } from 'react';
import { getCachedDecoded } from './peaksCache';
import { registerUserScopedReset } from './userScopedCaches';

export const LISTEN_PROGRESS_KEY = 'mg:listen-progress:v1';
const MAX_ENTRIES = 400;
const BARS = 96;
// Under this many seconds a play is a mis-click, not a listen.
export const MIN_HEARD_SEC = 3;
const CHANGE_EVENT = 'mg-listen-progress';

let store = null; // trackId -> record
let saveTimer = null;

function load() {
    if (store) return store;
    store = new Map();
    try {
        const raw = localStorage.getItem(LISTEN_PROGRESS_KEY);
        const obj = raw ? JSON.parse(raw) : null;
        if (obj && typeof obj === 'object') {
            for (const [k, v] of Object.entries(obj)) if (v && v.d > 0) store.set(k, v);
        }
    } catch { /* corrupt or blocked storage — start empty */ }
    return store;
}

function persistSoon() {
    if (saveTimer) return;
    saveTimer = setTimeout(persistNow, 4000);
}

function persistNow() {
    if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; }
    if (!store) return;
    let entries = [...store.entries()];
    if (entries.length > MAX_ENTRIES) {
        entries.sort((a, b) => (b[1].t || 0) - (a[1].t || 0));
        entries = entries.slice(0, MAX_ENTRIES);
        store = new Map(entries);
    }
    try { localStorage.setItem(LISTEN_PROGRESS_KEY, JSON.stringify(Object.fromEntries(entries))); } catch { /* quota / private */ }
}

registerUserScopedReset(() => {
    if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; }
    store = new Map();
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { all: true } }));
});

export function getListenProgress(trackId) {
    if (trackId == null) return null;
    return load().get(String(trackId)) || null;
}

// A listen worth showing: heard past the mis-click floor.
export function isHeard(rec) {
    return !!(rec && rec.d > 0 && rec.p >= MIN_HEARD_SEC);
}

// Peaks (any length) → BARS ints 0-99, normalized to the loudest bar.
export function summarizePeaks(peaks, n = BARS) {
    if (!peaks || !peaks.length) return null;
    const out = new Array(n).fill(0);
    const ratio = peaks.length / n;
    let max = 0;
    for (let i = 0; i < n; i++) {
        const s = Math.floor(i * ratio);
        const e = Math.max(s + 1, Math.floor((i + 1) * ratio));
        let m = 0;
        for (let j = s; j < e && j < peaks.length; j++) { const v = Math.abs(peaks[j]); if (v > m) m = v; }
        out[i] = m;
        if (m > max) max = m;
    }
    if (!(max > 0)) return null;
    return out.map((v) => Math.round((v / max) * 99));
}

// ── The active player's clock ──────────────────────────────────────────────
// Phones: MusicPlayer mirrors the native plugin's position here (the <audio>
// element is not what plays there).
export function setNativeClock(clock) { window.__mgNativeClock = clock; }

export function getPlayerClock() {
    const n = window.__mgNativeClock;
    if (n && n.active) return { t: n.position || 0, d: n.duration || 0, playing: !!n.playing };
    const a = window.musicPlayerInstance;
    if (!a || !a.src) return null;
    return { t: a.currentTime || 0, d: a.duration || 0, playing: !a.paused };
}

export function seekPlayer(sec) {
    const n = window.__mgNativeClock;
    if (n && n.active && typeof n.seek === 'function') { try { n.seek(sec); } catch { /* not fatal */ } return; }
    const a = window.musicPlayerInstance;
    if (a && a.duration > 0) { try { a.currentTime = sec; } catch { /* not fatal */ } }
}

// The track the player is on right now (null while a mix plays).
export function currentPlayerTrackId() {
    if (window.__currentPlayerMixId) return null;
    return window.__currentPlayerTrackId || null;
}

// ── Resume ─────────────────────────────────────────────────────────────────
// Set before a hero Play; the recorder seeks once that track is playing a
// source of the same length (a sample of a full-track listen never seeks).
let pendingResume = null;
export function requestResume(trackId, sec, expectedDur) {
    pendingResume = { tid: String(trackId), sec, dur: expectedDur, at: Date.now() };
}

// ── Recorder ───────────────────────────────────────────────────────────────
let recorderStarted = false;

function tick() {
    const clock = getPlayerClock();
    const tid = currentPlayerTrackId();
    if (pendingResume && Date.now() - pendingResume.at > 30000) pendingResume = null;
    if (!clock || !tid || !(clock.d > 0) || !Number.isFinite(clock.d)) return;

    if (pendingResume && pendingResume.tid === tid && clock.playing) {
        const r = pendingResume;
        pendingResume = null;
        const sameSource = !r.dur || Math.abs(clock.d - r.dur) / r.dur < 0.1;
        if (sameSource && r.sec > 1 && r.sec < clock.d - 5 && clock.t < r.sec) {
            seekPlayer(r.sec);
            return; // the next tick reads the post-seek position
        }
    }
    if (!clock.playing) return;

    const s = load();
    let rec = s.get(tid);
    // A different-length source (sample vs full track) starts a fresh record:
    // positions from one mean nothing on the other.
    if (rec && Math.abs(rec.d - clock.d) / clock.d > 0.1) rec = null;
    const t = Math.min(clock.t, clock.d);
    const p = Math.max(rec?.p || 0, t);
    const done = !!rec?.done || p >= clock.d * 0.95 || clock.d - p < 4;
    let b = rec?.b || null;
    if (!b) {
        const key = window.__currentPlayerOriginalUrl || window.__currentPlayerMusicSrc;
        const peaks = key ? getCachedDecoded(key) : null;
        if (peaks && peaks.peaks && (!peaks.duration || Math.abs(peaks.duration - clock.d) / clock.d < 0.1)) {
            b = summarizePeaks(peaks.peaks);
        }
    }
    const next = { p: Math.round(p * 10) / 10, l: Math.round(t * 10) / 10, d: Math.round(clock.d * 10) / 10, t: Date.now(), done, b };
    s.set(tid, next);
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { trackId: tid } }));
    persistSoon();
}

export function startListenRecorder() {
    if (recorderStarted || typeof window === 'undefined') return;
    recorderStarted = true;
    setInterval(tick, 1000);
    window.addEventListener('beforeunload', persistNow);
    document.addEventListener('visibilitychange', () => { if (document.hidden) persistNow(); });
}

// ── Hooks ──────────────────────────────────────────────────────────────────
export function useListenProgress(trackId) {
    const id = trackId != null ? String(trackId) : null;
    const [rec, setRec] = useState(() => getListenProgress(id));
    useEffect(() => {
        setRec(getListenProgress(id));
        if (!id) return undefined;
        const on = (e) => { if (e.detail?.all || e.detail?.trackId === id) setRec(getListenProgress(id)); };
        window.addEventListener(CHANGE_EVENT, on);
        return () => window.removeEventListener(CHANGE_EVENT, on);
    }, [id]);
    return rec;
}

// Whether this track is the player's current track (playing or paused), by
// id or by the sample url the player streams.
export function useIsCurrentTrack(trackId, src) {
    const match = () => {
        if (window.__currentPlayerMixId) return false;
        const tid = window.__currentPlayerTrackId;
        if (trackId != null && tid && tid === String(trackId)) return true;
        return !!(src && window.__currentPlayerMusicSrc === src);
    };
    const [cur, setCur] = useState(match);
    useEffect(() => {
        setCur(match());
        const on = () => setCur(match());
        window.addEventListener('player-track-change', on);
        return () => window.removeEventListener('player-track-change', on);
    }, [trackId, src]); // eslint-disable-line react-hooks/exhaustive-deps
    return cur;
}

export function formatClock(sec) {
    const s = Math.max(0, Math.floor(sec || 0));
    const m = Math.floor(s / 60);
    return `${m}:${String(s % 60).padStart(2, '0')}`;
}

// Test seam.
export const __test = { tick, reset: () => { store = null; pendingResume = null; }, persistNow };
